import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import AdminAPI from "../../services/AdminAPI";

const ACPPContext = createContext(null);

export const GRADE_ORDER = [
  "BV1","BV2","BV3",
  "1","2","3","4","5",
  "6","7","8","9",
  "10","11","12",
];

export const SECTION_BASE_LABELS = Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i));

const SDP_SETTINGS_KEY = "school_portal_settings";
const ACPP_SETTINGS_KEY = "acpp_portal_settings";
const MODULE1_EXTRA_KEY = "schoolMaster";
const MODULE3_DATA_KEY = "gradesOffered";
const MODULE4_DATA_KEY = "academicPlanning";
const MODULE5_DATA_KEY = "academicOwnership";
const MODULE6_DATA_KEY = "teacherPlanningWorkflow";
const MODULE7_DATA_KEY = "assessmentStructure";
const MODULE8_DATA_KEY = "teacherDevelopmentTraining";
const MODULE9_DATA_KEY = "studentDevelopmentPlan";
const MODULE10_DATA_KEY = "academicCalendar";
const MODULE11_DATA_KEY = "dynamicTimetable";
const MODULE12_DATA_KEY = "leadershipDashboard";
const MODULE6_TERMS = [1, 2, 3, 4];
const MODULE4_CATEGORY_ORDER = ["Academic", "Co-Curricular", "Vocational"];
const MODULE1_REQUIRED_SOURCED_FIELDS = [
  "name", "code", "year", "board", "strength",
  "principal", "email", "phone", "vision",
];
const SDP_IDENTITY_STORAGE_KEY = "school_identity_data";
const SDP_SCHOOL_INFO_STORAGE_KEY = "school_information_data";

function _safeStr(value) {
  return value === undefined || value === null ? "" : String(value);
}

function _hasValue(value) {
  return _safeStr(value).trim() !== "";
}

function _computeModule1Status(master) {
  const missingCount = MODULE1_REQUIRED_SOURCED_FIELDS.filter((field) => !_hasValue(master[field])).length;
  if (missingCount === MODULE1_REQUIRED_SOURCED_FIELDS.length) return "Pending";
  if (missingCount === 0 && _hasValue(master.priority)) return "Saved";
  return "In Progress";
}

function _isNotFoundError(error) {
  const msg = String(error?.message || error || "").toLowerCase();
  return msg.includes("404") || msg.includes("not found");
}

function _readJsonStorage(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
}

export function ACPPProvider({ children }) {
  const currentUserRole = _safeStr(localStorage.getItem("user_role")).toLowerCase() || "admin";
  const canEditModules1to5 = ["admin", "management"].includes(currentUserRole);
  const canEditModule6 = ["admin", "management", "hod"].includes(currentUserRole);

  const [acppAcademicYear, setAcppAcademicYear] = useState("");

  // Module 1 — School Master Details
  const [schoolMaster, setSchoolMaster] = useState({
    name: "", code: "", year: "", board: "",
    strength: "", principal: "", email: "", phone: "",
    vision: "", priority: "",
    status: "Pending",
  });
  const [module1Loading, setModule1Loading] = useState(false);
  const [module1LoadError, setModule1LoadError] = useState("");
  const [module1MissingFields, setModule1MissingFields] = useState([]);

  // Module 3 — Grades
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [module3Loading, setModule3Loading] = useState(false);
  const [module3LoadError, setModule3LoadError] = useState("");

  // Module 4 — Subject Mappings
  const [subjectMappings, setSubjectMappings] = useState([]);
  const [module4Loading, setModule4Loading] = useState(false);
  const [module4LoadError, setModule4LoadError] = useState("");

  // Module 5 — Section & Ownership Mappings
  const [module5SectionMappings, setModule5SectionMappings] = useState([]);
  const [module5OwnershipMappings, setModule5OwnershipMappings] = useState([]);
  const [module5Loading, setModule5Loading] = useState(false);
  const [module5LoadError, setModule5LoadError] = useState("");
  const [module5TeacherOptions, setModule5TeacherOptions] = useState([]);
  const [module5HodOptions, setModule5HodOptions] = useState([]);
  const [module5StaffLoading, setModule5StaffLoading] = useState(false);
  const [module5StaffLoadError, setModule5StaffLoadError] = useState("");
  const [module5ProvisionLoading, setModule5ProvisionLoading] = useState(false);
  const [module5ProvisionError, setModule5ProvisionError] = useState("");
  const [module7ProvisionLoading, setModule7ProvisionLoading] = useState(false);
  const [module7ProvisionError, setModule7ProvisionError] = useState("");

  // Module 6 — Teacher Planning
  const [module6Plans, setModule6Plans] = useState({});
  const [module6Loading, setModule6Loading] = useState(false);
  const [module6LoadError, setModule6LoadError] = useState("");
  const [module6Saving, setModule6Saving] = useState(false);
  const [module6SaveError, setModule6SaveError] = useState("");
  const [module6ActionLoading, setModule6ActionLoading] = useState(false);
  const [module6ActionError, setModule6ActionError] = useState("");

  // Module 7 — Assessments
  const [module7Assessments, setModule7Assessments] = useState([]);
  const [module7Loading, setModule7Loading] = useState(false);
  const [module7LoadError, setModule7LoadError] = useState("");

  // Module 8 — Teacher Development & Training
  const [module8DevPlans, setModule8DevPlans] = useState([]);
  const [module8TrainingPlans, setModule8TrainingPlans] = useState([]);
  const [module8Loading, setModule8Loading] = useState(false);
  const [module8LoadError, setModule8LoadError] = useState("");

  // Module 9 — Student Development
  const [module9DevPlans, setModule9DevPlans] = useState([]);
  const [module9Loading, setModule9Loading] = useState(false);
  const [module9LoadError, setModule9LoadError] = useState("");

  // Module 10 — Academic Calendar
  const [module10CalendarRows, setModule10CalendarRows] = useState([]);
  const [module10CustomEvents, setModule10CustomEvents] = useState([]);
  const [module10Loading, setModule10Loading] = useState(false);
  const [module10LoadError, setModule10LoadError] = useState("");

  // Module 11 — Timetable
  const [module11State, setModule11State] = useState({
    sectionTimetables: {},
    teacherTimetables: {},
    periodLabels: [],
    workingDays: [],
    periodCount: 6,
  });
  const [module11Loading, setModule11Loading] = useState(false);
  const [module11LoadError, setModule11LoadError] = useState("");

  // Module 12 — Leadership Dashboard Snapshot
  const [module12Snapshot, setModule12Snapshot] = useState({
    kpi: {},
    healthRows: [],
    chainSteps: [],
    summaryNotes: "",
    updatedAt: "",
    updatedBy: "",
  });
  const [module12Loading, setModule12Loading] = useState(false);
  const [module12LoadError, setModule12LoadError] = useState("");

  const getFallbackAcademicYear = useCallback(() => {
    if (_hasValue(acppAcademicYear)) return _safeStr(acppAcademicYear).trim();
    try {
      const acppRaw = localStorage.getItem(ACPP_SETTINGS_KEY);
      if (acppRaw) {
        const acppParsed = JSON.parse(acppRaw);
        if (_hasValue(acppParsed?.academicYear)) return acppParsed.academicYear;
      }
    } catch (_) {
      // Ignore malformed local storage values and fallback.
    }
    try {
      const raw = localStorage.getItem(SDP_SETTINGS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (_hasValue(parsed?.academicYear)) return parsed.academicYear;
      }
    } catch (_) {
      // Ignore malformed local storage values and fallback.
    }
    return _safeStr(schoolMaster.year).trim() || "2026-27";
  }, [acppAcademicYear, schoolMaster.year]);

  const getActiveAcademicYear = useCallback(() => {
    return _safeStr(acppAcademicYear).trim() || getFallbackAcademicYear();
  }, [acppAcademicYear, getFallbackAcademicYear]);

  const loadACPPAcademicYear = useCallback(async () => {
    let year = getFallbackAcademicYear();
    try {
      const settings = await AdminAPI.acppGetSettings();
      const apiYear = _safeStr(settings?.academic_year || settings?.academicYear).trim();
      if (_hasValue(apiYear)) year = apiYear;
    } catch (_) {
      // Keep fallback if settings endpoint fails.
    }

    setAcppAcademicYear(year);
    try {
      localStorage.setItem(ACPP_SETTINGS_KEY, JSON.stringify({ academicYear: year }));
    } catch (_) {
      // Ignore storage write errors.
    }
    return year;
  }, [getFallbackAcademicYear]);

  const resolveModule1MissingFields = useCallback((master) => {
    return MODULE1_REQUIRED_SOURCED_FIELDS.filter((field) => !_hasValue(master[field]));
  }, []);

  const mergeModule1FromSources = useCallback((identityData, schoolInfoData, extrasData) => {
    const merged = {
      name: _safeStr(schoolInfoData?.schoolName).trim(),
      code: _safeStr(schoolInfoData?.schoolCode).trim(),
      year: _safeStr(schoolInfoData?.academicYear).trim(),
      board: _safeStr(schoolInfoData?.board).trim(),
      strength: _safeStr(schoolInfoData?.studentStrength).trim(),
      principal: _safeStr(schoolInfoData?.principalName).trim(),
      email: _safeStr(schoolInfoData?.email).trim(),
      phone: _safeStr(schoolInfoData?.phone).trim(),
      vision: _safeStr(identityData?.vision).trim(),
      priority: _safeStr(extrasData?.priority).trim(),
    };
    return { ...merged, status: _computeModule1Status(merged) };
  }, []);

  const loadSchoolMasterFromSources = useCallback(async () => {
    setModule1Loading(true);
    setModule1LoadError("");

    const requestedYear = getActiveAcademicYear();
    let firstErrorMessage = "";
    let schoolInfoData = null;
    let identityData = null;
    let extrasData = null;
    let schoolInfoYear = "";

    const tryLoadSdpModule = async (moduleKey, years) => {
      const candidates = [...new Set(years.filter((y) => _hasValue(y)).map((y) => _safeStr(y).trim()))];
      for (const year of candidates) {
        try {
          const res = await AdminAPI.sdpGetModule(moduleKey, year);
          return { data: res, loadedYear: year };
        } catch (error) {
          if (!_isNotFoundError(error)) {
            throw error;
          }
        }
      }
      return { data: null, loadedYear: "" };
    };

    try {
      const schoolInfoLoad = await tryLoadSdpModule("schoolInfo", [requestedYear]);
      schoolInfoData = schoolInfoLoad.data;
      schoolInfoYear = _safeStr(schoolInfoData?.data?.academicYear).trim();

      if (!schoolInfoData) {
        const cachedSchoolInfo = _readJsonStorage(SDP_SCHOOL_INFO_STORAGE_KEY);
        if (cachedSchoolInfo) {
          schoolInfoData = { data: cachedSchoolInfo };
          schoolInfoYear = _safeStr(cachedSchoolInfo?.academicYear).trim();
        }
      }
    } catch (error) {
      if (!_isNotFoundError(error)) {
        firstErrorMessage = "Failed to load school profile from SDP School Information.";
      }
    }

    try {
      const identityLoad = await tryLoadSdpModule("identity", [requestedYear, schoolInfoYear]);
      identityData = identityLoad.data;

      if (!identityData) {
        const cachedIdentity = _readJsonStorage(SDP_IDENTITY_STORAGE_KEY);
        if (cachedIdentity) {
          identityData = { data: cachedIdentity };
        }
      }
    } catch (error) {
      if (!_isNotFoundError(error) && !firstErrorMessage) {
        firstErrorMessage = "Failed to load identity data from SDP Institutional Identity.";
      }
    }

    try {
      extrasData = await AdminAPI.acppGetModuleExtras(MODULE1_EXTRA_KEY, requestedYear);
    } catch (error) {
      // Optional data — ignore 404 and continue with defaults.
      if (!_isNotFoundError(error) && !firstErrorMessage) {
        firstErrorMessage = "Failed to load ACPP Module 1 extras.";
      }
    }

    const nextMaster = mergeModule1FromSources(
      identityData?.data || {},
      schoolInfoData?.data || {},
      extrasData || {}
    );
    setSchoolMaster(nextMaster);
    setModule1MissingFields(resolveModule1MissingFields(nextMaster));
    setModule1LoadError(firstErrorMessage);
    setModule1Loading(false);
    return nextMaster;
  }, [getActiveAcademicYear, mergeModule1FromSources, resolveModule1MissingFields]);

  const saveSchoolMasterPriority = useCallback(async (priorityValue) => {
    const academicYear = getActiveAcademicYear() || _safeStr(schoolMaster.year).trim();
    if (!_hasValue(academicYear)) {
      throw new Error("Academic year is missing. Please complete SDP School Information first.");
    }
    const cleanPriority = _safeStr(priorityValue).trim();
    await AdminAPI.acppSaveModuleExtras(MODULE1_EXTRA_KEY, academicYear, cleanPriority);

    const nextMaster = {
      ...schoolMaster,
      priority: cleanPriority,
    };
    nextMaster.status = _computeModule1Status(nextMaster);
    setSchoolMaster(nextMaster);
    setModule1MissingFields(resolveModule1MissingFields(nextMaster));
    return nextMaster;
  }, [schoolMaster, getActiveAcademicYear, resolveModule1MissingFields]);

  const resetSchoolMasterPriority = useCallback(async () => {
    const academicYear = getActiveAcademicYear() || _safeStr(schoolMaster.year).trim();
    if (_hasValue(academicYear)) {
      await AdminAPI.acppDeleteModuleExtras(MODULE1_EXTRA_KEY, academicYear);
    }
    const nextMaster = {
      ...schoolMaster,
      priority: "",
    };
    nextMaster.status = _computeModule1Status(nextMaster);
    setSchoolMaster(nextMaster);
    setModule1MissingFields(resolveModule1MissingFields(nextMaster));
    return nextMaster;
  }, [schoolMaster, getActiveAcademicYear, resolveModule1MissingFields]);

  const normalizeSelectedGrades = useCallback((grades) => {
    const set = new Set((grades || []).map((g) => _safeStr(g).trim()).filter(Boolean));
    return GRADE_ORDER.filter((grade) => set.has(grade));
  }, []);

  const normalizeSubjectMappings = useCallback((rows) => {
    const dedupe = (items) => {
      const seen = new Set();
      const out = [];
      (items || []).forEach((item) => {
        const clean = _safeStr(item).trim();
        if (clean && !seen.has(clean)) {
          seen.add(clean);
          out.push(clean);
        }
      });
      return out;
    };

    return (rows || []).map((row) => {
      const subjectName = _safeStr(row?.subjectName).trim();
      const gradesSet = new Set(dedupe(row?.grades));
      const grades = GRADE_ORDER.filter((g) => gradesSet.has(g));
      const categoriesSet = new Set(dedupe(row?.categories));
      const categories = MODULE4_CATEGORY_ORDER.filter((c) => categoriesSet.has(c));
      const types = dedupe(row?.types);
      const customType = _safeStr(row?.customType).trim();
      const finalTypes = dedupe([...types, ...(customType ? [customType] : [])]);
      const note = _safeStr(row?.note).trim();

      return {
        subjectName,
        grades,
        categories,
        types,
        customType,
        finalTypes,
        note,
      };
    });
  }, []);

  const normalizeSectionMappings = useCallback((rows) => {
    const dedupe = (items) => {
      const seen = new Set();
      const out = [];
      (items || []).forEach((item) => {
        const clean = _safeStr(item).trim();
        if (clean && !seen.has(clean)) {
          seen.add(clean);
          out.push(clean);
        }
      });
      return out;
    };

    const parseCustomSections = (value) => {
      return dedupe(
        _safeStr(value)
          .split(",")
          .map((part) => {
            const token = part.trim();
            if (!token) return "";
            if (token.includes("=")) return token.split("=").slice(1).join("=").trim();
            return token;
          })
          .filter(Boolean)
      );
    };

    return (rows || []).map((row) => {
      const grade = _safeStr(row?.grade).trim();
      const presetSet = new Set(dedupe(row?.presetSections));
      const presetSections = [...SECTION_BASE_LABELS.filter((label) => presetSet.has(label))]
        .concat(dedupe(row?.presetSections).filter((label) => !SECTION_BASE_LABELS.includes(label)).sort());
      const customSectionsInput = _safeStr(row?.customSectionsInput || row?.customInput).trim();
      const customSections = dedupe(row?.customSections).length
        ? dedupe(row?.customSections)
        : parseCustomSections(customSectionsInput);
      const finalSections = dedupe([...presetSections, ...customSections]);
      const class_owner_teacher_id = _safeStr(row?.class_owner_teacher_id).trim();
      const class_owner_teacher_name = _safeStr(row?.class_owner_teacher_name).trim();
      return {
        grade,
        presetSections,
        customSectionsInput,
        customSections,
        finalSections,
        class_owner_teacher_id,
        class_owner_teacher_name,
      };
    });
  }, []);

  const normalizeOwnershipMappings = useCallback((rows) => {
    const dedupe = (items) => {
      const seen = new Set();
      const out = [];
      (items || []).forEach((item) => {
        const clean = _safeStr(item).trim();
        if (clean && !seen.has(clean)) {
          seen.add(clean);
          out.push(clean);
        }
      });
      return out;
    };

    return (rows || []).map((row, index) => {
      const grade = _safeStr(row?.grade).trim();
      const section = _safeStr(row?.section).trim();
      const subject = _safeStr(row?.subject).trim();
      const uid = _safeStr(row?.uid).trim() || `${grade}__${section}__${subject}__${index + 1}`;
      const teacher_name = _safeStr(row?.teacher_name || (row?.teachers || [])[0]).trim();
      const hod_name = _safeStr(row?.hod_name || (row?.hods || [])[0]).trim();
      return {
        uid,
        grade,
        section,
        subject,
        department_name: _safeStr(row?.department_name).trim(),
        teacher_id: _safeStr(row?.teacher_id).trim(),
        teacher_name,
        hod_id: _safeStr(row?.hod_id).trim(),
        hod_name,
        categories: [...new Set((row?.categories || []).map((c) => _safeStr(c).trim()).filter(Boolean))],
        types: [...new Set((row?.types || []).map((t) => _safeStr(t).trim()).filter(Boolean))],
        teachers: teacher_name ? [teacher_name] : dedupe(row?.teachers).slice(0, 1),
        hods: hod_name ? [hod_name] : dedupe(row?.hods).slice(0, 1),
      };
    });
  }, []);

  const normalizeStaffOptions = useCallback((items) => {
    const seen = new Set();
    const out = [];
    (items || []).forEach((item) => {
      const id = _safeStr(item?.id).trim();
      if (!id || seen.has(id)) return;
      seen.add(id);
      out.push({
        id,
        name: _safeStr(item?.name).trim(),
        email: _safeStr(item?.email).trim(),
      });
    });
    return out.sort((a, b) => a.name.localeCompare(b.name));
  }, []);

  const normalizeModule6Plans = useCallback((plansObj) => {
    const dedupe = (items) => {
      const seen = new Set();
      const out = [];
      (items || []).forEach((item) => {
        const clean = _safeStr(item).trim();
        if (clean && !seen.has(clean)) {
          seen.add(clean);
          out.push(clean);
        }
      });
      return out;
    };

    const normalizeTopic = (topic) => ({
      id: _safeStr(topic?.id).trim(),
      topicName: _safeStr(topic?.topicName).trim(),
      subtopics: dedupe(topic?.subtopics),
    });

    const normalizeChapter = (chapter) => ({
      id: _safeStr(chapter?.id).trim(),
      chapterNo: _safeStr(chapter?.chapterNo).trim(),
      chapterName: _safeStr(chapter?.chapterName).trim(),
      topics: (chapter?.topics || []).map(normalizeTopic),
      pedagogies: dedupe(chapter?.pedagogies),
      customPedagogies: dedupe(chapter?.customPedagogies),
      learningObjectives: dedupe(chapter?.learningObjectives),
      competencies: dedupe(chapter?.competencies),
      customCompetencies: dedupe(chapter?.customCompetencies),
    });

    const normalizeUnit = (unit) => ({
      id: _safeStr(unit?.id).trim(),
      unitNo: _safeStr(unit?.unitNo).trim(),
      unitName: _safeStr(unit?.unitName).trim(),
      chapters: (unit?.chapters || []).map(normalizeChapter),
    });

    const normalizeTerms = (terms) => {
      const byNo = new Map();
      (terms || []).forEach((term) => {
        const num = Number(term?.termNo);
        if ([1, 2, 3, 4].includes(num)) {
          byNo.set(num, {
            termNo: num,
            units: (term?.units || []).map(normalizeUnit),
          });
        }
      });
      return [1, 2, 3, 4].map((termNo) => byNo.get(termNo) || { termNo, units: [] });
    };

    const source = plansObj && typeof plansObj === "object" ? plansObj : {};
    const out = {};
    Object.entries(source).forEach(([fallbackUid, raw]) => {
      if (!raw || typeof raw !== "object") return;
      const uid = _safeStr(raw.uid).trim() || _safeStr(fallbackUid).trim();
      if (!uid) return;
      const teacherStatusRaw = _safeStr(raw.teacher_status || raw.teacherStatus).trim();
      let teacherStatus = teacherStatusRaw === "Submitted" ? "Submitted" : "Draft";
      const hodStatusRaw = _safeStr(raw.hod_status || raw.hodStatus).trim();
      let hodStatus = [
        "Pending",
        "Under Review",
        "Submitted To Admin",
        "Sent Back To Teacher",
        "Approved",
        "Sent Back",
      ].includes(hodStatusRaw)
        ? hodStatusRaw
        : "Pending";
      const adminStatusRaw = _safeStr(raw.admin_status || raw.adminStatus).trim();
      let adminStatus = [
        "Pending",
        "Under Review",
        "Approved",
        "Sent Back To HOD",
      ].includes(adminStatusRaw)
        ? adminStatusRaw
        : "Pending";
      if (adminStatus === "Approved") {
        teacherStatus = "Submitted";
        if (hodStatus === "Pending" || hodStatus === "Under Review") hodStatus = "Submitted To Admin";
      } else if (adminStatus === "Under Review") {
        teacherStatus = "Submitted";
        if (hodStatus === "Pending") hodStatus = "Submitted To Admin";
      }
      if (hodStatus === "Submitted To Admin" && adminStatus === "Pending") {
        adminStatus = "Under Review";
      }
      if (hodStatus === "Sent Back To Teacher") {
        teacherStatus = "Draft";
        if (adminStatus !== "Sent Back To HOD") adminStatus = "Pending";
      }
      const workflowStageRaw = _safeStr(raw.workflow_stage || raw.workflowStage).trim();
      const workflowStage = ["teacher_draft", "hod_review", "admin_review", "approved"].includes(workflowStageRaw)
        ? workflowStageRaw
        : (
            adminStatus === "Approved"
              ? "approved"
              : (adminStatus === "Under Review" || hodStatus === "Submitted To Admin")
                ? "admin_review"
                : (hodStatus === "Under Review" || teacherStatus === "Submitted")
                  ? "hod_review"
                  : "teacher_draft"
          );

      out[uid] = {
        uid,
        academicYear: _safeStr(raw.academicYear).trim() || getActiveAcademicYear(),
        grade: _safeStr(raw.grade).trim(),
        section: _safeStr(raw.section).trim(),
        subject: _safeStr(raw.subject).trim(),
        teachers: dedupe(raw.teachers),
        hods: dedupe(raw.hods),
        categories: [...new Set((raw.categories || []).map((c) => _safeStr(c).trim()).filter(Boolean))],
        types: [...new Set((raw.types || []).map((t) => _safeStr(t).trim()).filter(Boolean))],
        teacherStatus,
        teacher_status: teacherStatus,
        hodStatus,
        hod_status: hodStatus,
        adminStatus,
        admin_status: adminStatus,
        workflowStage,
        workflow_stage: workflowStage,
        teacherSubmittedAt: _safeStr(raw.teacherSubmittedAt).trim(),
        teacherSubmittedBy: _safeStr(raw.teacherSubmittedBy).trim(),
        hodSubmittedAt: _safeStr(raw.hodSubmittedAt).trim(),
        hodSubmittedBy: _safeStr(raw.hodSubmittedBy).trim(),
        adminApprovedAt: _safeStr(raw.adminApprovedAt).trim(),
        adminApprovedBy: _safeStr(raw.adminApprovedBy).trim(),
        adminRemarks: _safeStr(raw.adminRemarks).trim(),
        hodRemarks: _safeStr(raw.hodRemarks).trim(),
        remarksThread: Array.isArray(raw.remarksThread) ? raw.remarksThread : [],
        // Server-owned workflow metadata; the server ignores these on save.
        revision: Number(raw.revision) > 0 ? Number(raw.revision) : 1,
        status_history: Array.isArray(raw.status_history) ? raw.status_history : [],
        last_changes_requested:
          raw.last_changes_requested && typeof raw.last_changes_requested === "object" ? raw.last_changes_requested : null,
        display_status: _safeStr(raw.display_status).trim() || workflowStage,
        submittedAt: _safeStr(raw.submittedAt).trim(),
        approvedAt: _safeStr(raw.approvedAt).trim(),
        lastUpdated: _safeStr(raw.lastUpdated).trim(),
        terms: normalizeTerms(raw.terms),
      };
    });
    return out;
  }, [getActiveAcademicYear]);

  const normalizeModule7Assessments = useCallback((rows) => {
    const dedupe = (items) => {
      const seen = new Set();
      const out = [];
      (items || []).forEach((item) => {
        const clean = _safeStr(item).trim();
        if (clean && !seen.has(clean)) {
          seen.add(clean);
          out.push(clean);
        }
      });
      return out;
    };

    return (rows || []).map((row) => ({
      rowId: _safeStr(row?.rowId || row?.row_id).trim(),
      academicYear: _safeStr(row?.academicYear).trim() || getActiveAcademicYear(),
      grade: _safeStr(row?.grade).trim(),
      section: _safeStr(row?.section).trim(),
      subject: _safeStr(row?.subject).trim(),
      domain: _safeStr(row?.domain).trim(),
      title: _safeStr(row?.title).trim(),
      duration: _safeStr(row?.duration).trim(),
      totalMarks: _safeStr(row?.totalMarks).trim(),
      difficulty: _safeStr(row?.difficulty).trim() || "medium",
      numQuestions: _safeStr(row?.numQuestions).trim(),
      tenure: _safeStr(row?.tenure).trim(),
      assessmentType: _safeStr(row?.assessmentType).trim(),
      assessmentSubTypes: dedupe(row?.assessmentSubTypes),
      unit: _safeStr(row?.unit || row?.unitName).trim(),
      chapter: _safeStr(row?.chapter).trim(),
      competencies: dedupe(row?.competencies),
      chaptersCovered: dedupe(row?.chaptersCovered),
      unitNo: _safeStr(row?.unitNo).trim(),
      unitName: _safeStr(row?.unitName || row?.unit).trim(),
    }));
  }, [getActiveAcademicYear]);

  const normalizeModule8DevPlans = useCallback((rows) => {
    return (rows || []).map((row) => ({
      priorityArea: _safeStr(row?.priorityArea).trim(),
      problemIdentified: _safeStr(row?.problemIdentified).trim(),
      rootCause: _safeStr(row?.rootCause).trim(),
      actionToBeTaken: _safeStr(row?.actionToBeTaken).trim(),
      executionType: _safeStr(row?.executionType).trim(),
      responsible: _safeStr(row?.responsible).trim(),
      frequency: _safeStr(row?.frequency).trim(),
      timeline: _safeStr(row?.timeline).trim(),
      successMetric: _safeStr(row?.successMetric).trim(),
      status: _safeStr(row?.status).trim(),
      needTraining: _safeStr(row?.needTraining).trim(),
      reviewDate: _safeStr(row?.reviewDate).trim(),
    }));
  }, []);

  const normalizeModule8TrainingPlans = useCallback((rows) => {
    return (rows || []).map((row) => ({
      trainingFor: _safeStr(row?.trainingFor).trim(),
      trainingNeedArea: _safeStr(row?.trainingNeedArea).trim(),
      trainingTitle: _safeStr(row?.trainingTitle).trim(),
      trainingProvider: _safeStr(row?.trainingProvider).trim(),
      mode: _safeStr(row?.mode).trim(),
      deadline: _safeStr(row?.deadline).trim(),
      nominationStatus: _safeStr(row?.nominationStatus).trim(),
      completionStatus: _safeStr(row?.completionStatus).trim(),
      followupAction: _safeStr(row?.followupAction).trim(),
    }));
  }, []);

  const normalizeModule9DevPlans = useCallback((rows) => {
    return (rows || []).map((row) => ({
      priorityArea: _safeStr(row?.priorityArea).trim(),
      problemIdentified: _safeStr(row?.problemIdentified).trim(),
      rootCause: _safeStr(row?.rootCause).trim(),
      actionToBeTaken: _safeStr(row?.actionToBeTaken).trim(),
      executionType: _safeStr(row?.executionType).trim(),
      responsible: _safeStr(row?.responsible).trim(),
      frequency: _safeStr(row?.frequency).trim(),
      timeline: _safeStr(row?.timeline).trim(),
      successMetric: _safeStr(row?.successMetric).trim(),
      status: _safeStr(row?.status).trim(),
      deadline: _safeStr(row?.deadline).trim(),
      reviewDate: _safeStr(row?.reviewDate).trim(),
    }));
  }, []);

  const normalizeModule10CalendarRows = useCallback((rows) => {
    return (rows || []).map((row) => ({
      academicYear: _safeStr(row?.academicYear).trim(),
      grade: _safeStr(row?.grade).trim(),
      section: _safeStr(row?.section).trim(),
      subject: _safeStr(row?.subject).trim(),
      term: _safeStr(row?.term).trim(),
      unitNo: _safeStr(row?.unitNo).trim(),
      unitName: _safeStr(row?.unitName).trim(),
      chapter: _safeStr(row?.chapter).trim(),
      assessmentTenure: _safeStr(row?.assessmentTenure).trim(),
      assessmentType: _safeStr(row?.assessmentType).trim(),
      assessmentSubTypes: _safeStr(row?.assessmentSubTypes).trim(),
      eventDate: _safeStr(row?.eventDate).trim(),
      eventTitle: _safeStr(row?.eventTitle).trim(),
    }));
  }, []);

  const normalizeModule10CustomEvents = useCallback((events) => {
    return (events || []).map((ev) => ({
      id: _safeStr(ev?.id).trim() || `custom_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      title: _safeStr(ev?.title).trim(),
      date: _safeStr(ev?.date).trim(),
      eventType: _safeStr(ev?.eventType).trim() || "Other",
      notes: _safeStr(ev?.notes).trim(),
      academicYear: _safeStr(ev?.academicYear).trim(),
    }));
  }, []);

  const normalizeModule11State = useCallback((state) => {
    const normalized = state && typeof state === "object" ? state : {};
    const periodLabels = Array.isArray(normalized.periodLabels)
      ? normalized.periodLabels.map((label, idx) => _safeStr(label).trim() || `Period ${idx + 1}`)
      : [];
    const workingDays = Array.isArray(normalized.workingDays)
      ? normalized.workingDays.map((day) => _safeStr(day).trim()).filter(Boolean)
      : [];

    const safePeriodLabels = periodLabels.length ? periodLabels : Array.from({ length: 6 }, (_, i) => `Period ${i + 1}`);
    const safeWorkingDays = workingDays.length ? workingDays : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];

    const normalizeGrid = (grid) => {
      const rawGrid = grid && typeof grid === "object" ? grid : {};
      const outGrid = {};
      safeWorkingDays.forEach((day) => {
        const dayRow = rawGrid[day] && typeof rawGrid[day] === "object" ? rawGrid[day] : {};
        outGrid[day] = {};
        safePeriodLabels.forEach((period) => {
          outGrid[day][period] = _safeStr(dayRow[period]).trim();
        });
      });
      return outGrid;
    };

    const normalizeMap = (map) => {
      if (!map || typeof map !== "object") return {};
      const out = {};
      Object.entries(map).forEach(([key, value]) => {
        const item = value && typeof value === "object" ? value : {};
        out[key] = {
          entity: _safeStr(item.entity).trim() || _safeStr(key).trim(),
          grid: normalizeGrid(item.grid),
        };
      });
      return out;
    };

    const periodCountRaw = Number(normalized.periodCount);
    const periodCount = Number.isFinite(periodCountRaw)
      ? Math.max(1, Math.min(12, Math.trunc(periodCountRaw)))
      : safePeriodLabels.length;

    return {
      sectionTimetables: normalizeMap(normalized.sectionTimetables),
      teacherTimetables: normalizeMap(normalized.teacherTimetables),
      periodLabels: safePeriodLabels,
      workingDays: safeWorkingDays,
      periodCount,
    };
  }, []);

  const normalizeModule12Snapshot = useCallback((state) => {
    const normalized = state && typeof state === "object" ? state : {};
    const rawKpi = normalized.kpi && typeof normalized.kpi === "object" ? normalized.kpi : {};
    const kpi = {};
    Object.entries(rawKpi).forEach(([key, value]) => {
      const safeKey = _safeStr(key).trim();
      if (!safeKey) return;
      kpi[safeKey] = typeof value === "number" ? value : _safeStr(value).trim();
    });
    const healthRows = Array.isArray(normalized.healthRows)
      ? normalized.healthRows.map((row) => ({
          mod: _safeStr(row?.mod).trim(),
          name: _safeStr(row?.name).trim(),
          ok: Boolean(row?.ok),
          count: _safeStr(row?.count).trim(),
          note: _safeStr(row?.note).trim(),
        }))
      : [];
    const chainSteps = Array.isArray(normalized.chainSteps)
      ? normalized.chainSteps.map((row) => ({
          label: _safeStr(row?.label).trim(),
          ok: Boolean(row?.ok),
        }))
      : [];
    return {
      kpi,
      healthRows,
      chainSteps,
      summaryNotes: _safeStr(normalized.summaryNotes).trim(),
      updatedAt: _safeStr(normalized.updatedAt).trim(),
      updatedBy: _safeStr(normalized.updatedBy).trim(),
    };
  }, []);

  const loadModule3Grades = useCallback(async () => {
    setModule3Loading(true);
    setModule3LoadError("");
    const academicYear = getActiveAcademicYear();
    let nextSelected = [];
    try {
      const res = await AdminAPI.acppGetModuleData(MODULE3_DATA_KEY, academicYear);
      nextSelected = normalizeSelectedGrades(res?.selected_grades || []);
    } catch (error) {
      if (!_isNotFoundError(error)) {
        setModule3LoadError("Failed to load saved grades for this ACPP year.");
      }
    }
    setSelectedGrades(nextSelected);
    setModule3Loading(false);
    return nextSelected;
  }, [getActiveAcademicYear, normalizeSelectedGrades]);

  const saveModule3Grades = useCallback(async (grades) => {
    const academicYear = getActiveAcademicYear();
    if (!_hasValue(academicYear)) {
      throw new Error("ACPP academic year is missing. Please create or activate an ACPP plan.");
    }
    const normalized = normalizeSelectedGrades(grades);
    await AdminAPI.acppSaveModuleData(MODULE3_DATA_KEY, academicYear, {
      selected_grades: normalized,
    });
    setSelectedGrades(normalized);
    return normalized;
  }, [getActiveAcademicYear, normalizeSelectedGrades]);

  const resetModule3Grades = useCallback(async () => {
    const academicYear = getActiveAcademicYear();
    if (_hasValue(academicYear)) {
      await AdminAPI.acppDeleteModuleData(MODULE3_DATA_KEY, academicYear);
    }
    setSelectedGrades([]);
    return [];
  }, [getActiveAcademicYear]);

  const loadModule4AcademicPlanning = useCallback(async () => {
    setModule4Loading(true);
    setModule4LoadError("");
    const academicYear = getActiveAcademicYear();
    let nextMappings = [];
    try {
      const res = await AdminAPI.acppGetModuleData(MODULE4_DATA_KEY, academicYear);
      nextMappings = normalizeSubjectMappings(res?.subject_mappings || []);
    } catch (error) {
      if (!_isNotFoundError(error)) {
        setModule4LoadError("Failed to load saved academic planning for this ACPP year.");
      }
    }
    setSubjectMappings(nextMappings);
    setModule4Loading(false);
    return nextMappings;
  }, [getActiveAcademicYear, normalizeSubjectMappings]);

  const saveModule4AcademicPlanning = useCallback(async (rows) => {
    const academicYear = getActiveAcademicYear();
    if (!_hasValue(academicYear)) {
      throw new Error("ACPP academic year is missing. Please create or activate an ACPP plan.");
    }
    const normalized = normalizeSubjectMappings(rows);
    await AdminAPI.acppSaveModuleData(MODULE4_DATA_KEY, academicYear, {
      subject_mappings: normalized,
    });
    setSubjectMappings(normalized);
    return normalized;
  }, [getActiveAcademicYear, normalizeSubjectMappings]);

  const resetModule4AcademicPlanning = useCallback(async () => {
    const academicYear = getActiveAcademicYear();
    if (_hasValue(academicYear)) {
      await AdminAPI.acppDeleteModuleData(MODULE4_DATA_KEY, academicYear);
    }
    setSubjectMappings([]);
    return [];
  }, [getActiveAcademicYear]);

  const loadModule5AcademicOwnership = useCallback(async () => {
    setModule5Loading(true);
    setModule5LoadError("");
    const academicYear = getActiveAcademicYear();
    let nextSectionMappings = [];
    let nextOwnershipMappings = [];
    try {
      const res = await AdminAPI.acppGetModuleData(MODULE5_DATA_KEY, academicYear);
      nextSectionMappings = normalizeSectionMappings(res?.section_mappings || []);
      nextOwnershipMappings = normalizeOwnershipMappings(res?.ownership_mappings || []);
    } catch (error) {
      if (!_isNotFoundError(error)) {
        setModule5LoadError("Failed to load saved academic ownership mapping for this ACPP year.");
      }
    }
    setModule5SectionMappings(nextSectionMappings);
    setModule5OwnershipMappings(nextOwnershipMappings);
    setModule5Loading(false);
    return {
      sectionMappings: nextSectionMappings,
      ownershipMappings: nextOwnershipMappings,
    };
  }, [getActiveAcademicYear, normalizeSectionMappings, normalizeOwnershipMappings]);

  const loadModule5StaffOptions = useCallback(async () => {
    setModule5StaffLoading(true);
    setModule5StaffLoadError("");
    try {
      const res = await AdminAPI.acppGetReferenceStaff();
      setModule5TeacherOptions(normalizeStaffOptions(res?.teachers || []));
      setModule5HodOptions(normalizeStaffOptions(res?.hods || []));
    } catch (_) {
      setModule5StaffLoadError("Failed to load teachers and HODs from school users.");
      setModule5TeacherOptions([]);
      setModule5HodOptions([]);
    }
    setModule5StaffLoading(false);
  }, [normalizeStaffOptions]);

  const saveModule5AcademicOwnership = useCallback(async ({ sectionMappings, ownershipMappings }) => {
    const academicYear = getActiveAcademicYear();
    if (!_hasValue(academicYear)) {
      throw new Error("ACPP academic year is missing. Please create or activate an ACPP plan.");
    }
    const normalizedSections = normalizeSectionMappings(sectionMappings || []);
    const normalizedOwnership = normalizeOwnershipMappings(ownershipMappings || []);
    await AdminAPI.acppSaveModuleData(MODULE5_DATA_KEY, academicYear, {
      section_mappings: normalizedSections,
      ownership_mappings: normalizedOwnership,
    });
    setModule5SectionMappings(normalizedSections);
    setModule5OwnershipMappings(normalizedOwnership);
    return {
      sectionMappings: normalizedSections,
      ownershipMappings: normalizedOwnership,
    };
  }, [getActiveAcademicYear, normalizeSectionMappings, normalizeOwnershipMappings]);

  const resetModule5AcademicOwnership = useCallback(async () => {
    const academicYear = getActiveAcademicYear();
    if (_hasValue(academicYear)) {
      await AdminAPI.acppDeleteModuleData(MODULE5_DATA_KEY, academicYear);
    }
    setModule5SectionMappings([]);
    setModule5OwnershipMappings([]);
    return { sectionMappings: [], ownershipMappings: [] };
  }, [getActiveAcademicYear]);

  const loadModule6TeacherWorkflow = useCallback(async () => {
    setModule6Loading(true);
    setModule6LoadError("");
    const academicYear = getActiveAcademicYear();
    let nextPlans = {};
    try {
      const res = await AdminAPI.acppGetModuleData(MODULE6_DATA_KEY, academicYear);
      nextPlans = normalizeModule6Plans(res?.plans || {});
    } catch (error) {
      if (!_isNotFoundError(error)) {
        setModule6LoadError("Failed to load saved teacher planning for this ACPP year.");
      }
    }
    setModule6Plans(nextPlans);
    setModule6Loading(false);
    return nextPlans;
  }, [getActiveAcademicYear, normalizeModule6Plans]);

  const saveModule6TeacherWorkflow = useCallback(async (plans) => {
    const academicYear = getActiveAcademicYear();
    if (!_hasValue(academicYear)) {
      throw new Error("ACPP academic year is missing. Please create or activate an ACPP plan.");
    }
    setModule6Saving(true);
    setModule6SaveError("");
    try {
      const normalizedPlans = normalizeModule6Plans(plans || {});
      const res = await AdminAPI.acppSaveModuleData(MODULE6_DATA_KEY, academicYear, { plans: normalizedPlans });
      // Rows the server refused (wrong stage / not this user's / incomplete)
      // come back in `results`; reload so the screen shows what is stored.
      const outcomes = Object.values((res && res.results) || {});
      if (outcomes.some((o) => o === "locked" || o === "invalid")) {
        await loadModule6TeacherWorkflow();
        throw new Error(
          outcomes.includes("invalid")
            ? "Some chapters are incomplete, so this approved plan could not be sent back for re-approval. Fill in the missing details and save again."
            : "A plan is no longer editable at its current stage, so your changes to it were not saved."
        );
      }
      // Not replacing state with the snapshot we sent: the user may have kept typing.
      return normalizedPlans;
    } catch (error) {
      setModule6SaveError(error?.message || "Failed to save Module 6 data.");
      throw error;
    } finally {
      setModule6Saving(false);
    }
  }, [getActiveAcademicYear, normalizeModule6Plans, loadModule6TeacherWorkflow]);

  const resetModule6TeacherWorkflow = useCallback(async () => {
    const academicYear = getActiveAcademicYear();
    if (_hasValue(academicYear)) {
      await AdminAPI.acppDeleteModuleData(MODULE6_DATA_KEY, academicYear);
    }
    setModule6Plans({});
    setModule6SaveError("");
    return {};
  }, [getActiveAcademicYear]);

  const module6HodSubmitToAdmin = useCallback(async (uid, remarks = "") => {
    const academicYear = getActiveAcademicYear();
    setModule6ActionLoading(true);
    setModule6ActionError("");
    try {
      await AdminAPI.acppHodSubmitModule6ToAdmin({ academic_year: academicYear, uid, remarks });
      await loadModule6TeacherWorkflow();
    } catch (error) {
      setModule6ActionError(error?.message || "Failed to submit Module 6 plan to admin.");
      throw error;
    } finally {
      setModule6ActionLoading(false);
    }
  }, [getActiveAcademicYear, loadModule6TeacherWorkflow]);

  const module6HodSendBackToTeacher = useCallback(async (uid, remarks = "") => {
    const academicYear = getActiveAcademicYear();
    setModule6ActionLoading(true);
    setModule6ActionError("");
    try {
      await AdminAPI.acppHodSendBackModule6ToTeacher({ academic_year: academicYear, uid, remarks });
      await loadModule6TeacherWorkflow();
    } catch (error) {
      setModule6ActionError(error?.message || "Failed to send Module 6 plan back to teacher.");
      throw error;
    } finally {
      setModule6ActionLoading(false);
    }
  }, [getActiveAcademicYear, loadModule6TeacherWorkflow]);

  const module6AdminApprove = useCallback(async (uid, remarks = "") => {
    const academicYear = getActiveAcademicYear();
    setModule6ActionLoading(true);
    setModule6ActionError("");
    try {
      await AdminAPI.acppAdminApproveModule6({ academic_year: academicYear, uid, remarks });
      await loadModule6TeacherWorkflow();
    } catch (error) {
      setModule6ActionError(error?.message || "Failed to approve Module 6 plan.");
      throw error;
    } finally {
      setModule6ActionLoading(false);
    }
  }, [getActiveAcademicYear, loadModule6TeacherWorkflow]);

  const module6AdminSendBackToHod = useCallback(async (uid, remarks = "") => {
    const academicYear = getActiveAcademicYear();
    setModule6ActionLoading(true);
    setModule6ActionError("");
    try {
      await AdminAPI.acppAdminSendBackModule6ToHod({ academic_year: academicYear, uid, remarks });
      await loadModule6TeacherWorkflow();
    } catch (error) {
      setModule6ActionError(error?.message || "Failed to send Module 6 plan back to HOD.");
      throw error;
    } finally {
      setModule6ActionLoading(false);
    }
  }, [getActiveAcademicYear, loadModule6TeacherWorkflow]);

  const module6SubmitToHod = useCallback(async () => {
    throw new Error("Teacher submit action is not available in Admin panel.");
  }, []);

  const loadModule7AssessmentStructure = useCallback(async () => {
    setModule7Loading(true);
    setModule7LoadError("");
    const academicYear = getActiveAcademicYear();
    let nextRows = [];
    try {
      const res = await AdminAPI.acppGetModuleData(MODULE7_DATA_KEY, academicYear);
      nextRows = normalizeModule7Assessments(res?.assessments || []);
    } catch (error) {
      if (!_isNotFoundError(error)) {
        setModule7LoadError("Failed to load saved assessment structure for this ACPP year.");
      }
    }
    setModule7Assessments(nextRows);
    setModule7Loading(false);
    return nextRows;
  }, [getActiveAcademicYear, normalizeModule7Assessments]);

  const saveModule7AssessmentStructure = useCallback(async (rows) => {
    const academicYear = getActiveAcademicYear();
    if (!_hasValue(academicYear)) {
      throw new Error("ACPP academic year is missing. Please create or activate an ACPP plan.");
    }
    const normalizedRows = normalizeModule7Assessments(rows || []);
    await AdminAPI.acppSaveModuleData(MODULE7_DATA_KEY, academicYear, {
      assessments: normalizedRows,
    });
    setModule7Assessments(normalizedRows);
    return normalizedRows;
  }, [getActiveAcademicYear, normalizeModule7Assessments]);

  const resetModule7AssessmentStructure = useCallback(async () => {
    const academicYear = getActiveAcademicYear();
    if (_hasValue(academicYear)) {
      await AdminAPI.acppDeleteModuleData(MODULE7_DATA_KEY, academicYear);
    }
    setModule7Assessments([]);
    return [];
  }, [getActiveAcademicYear]);

  const loadModule8TeacherDevelopmentTraining = useCallback(async () => {
    setModule8Loading(true);
    setModule8LoadError("");
    const academicYear = getActiveAcademicYear();
    let nextDevPlans = [];
    let nextTrainingPlans = [];
    try {
      const res = await AdminAPI.acppGetModuleData(MODULE8_DATA_KEY, academicYear);
      nextDevPlans = normalizeModule8DevPlans(res?.dev_plans || []);
      nextTrainingPlans = normalizeModule8TrainingPlans(res?.training_plans || []);
    } catch (error) {
      if (!_isNotFoundError(error)) {
        setModule8LoadError("Failed to load saved teacher development/training data for this ACPP year.");
      }
    }
    setModule8DevPlans(nextDevPlans);
    setModule8TrainingPlans(nextTrainingPlans);
    setModule8Loading(false);
    return { devPlans: nextDevPlans, trainingPlans: nextTrainingPlans };
  }, [getActiveAcademicYear, normalizeModule8DevPlans, normalizeModule8TrainingPlans]);

  const saveModule8TeacherDevelopmentTraining = useCallback(async ({ devPlans, trainingPlans }) => {
    const academicYear = getActiveAcademicYear();
    if (!_hasValue(academicYear)) {
      throw new Error("ACPP academic year is missing. Please create or activate an ACPP plan.");
    }
    const normalizedDevPlans = normalizeModule8DevPlans(devPlans || []);
    const normalizedTrainingPlans = normalizeModule8TrainingPlans(trainingPlans || []);
    await AdminAPI.acppSaveModuleData(MODULE8_DATA_KEY, academicYear, {
      dev_plans: normalizedDevPlans,
      training_plans: normalizedTrainingPlans,
    });
    setModule8DevPlans(normalizedDevPlans);
    setModule8TrainingPlans(normalizedTrainingPlans);
    return { devPlans: normalizedDevPlans, trainingPlans: normalizedTrainingPlans };
  }, [getActiveAcademicYear, normalizeModule8DevPlans, normalizeModule8TrainingPlans]);

  const resetModule8TeacherDevelopmentTraining = useCallback(async () => {
    const academicYear = getActiveAcademicYear();
    if (_hasValue(academicYear)) {
      await AdminAPI.acppDeleteModuleData(MODULE8_DATA_KEY, academicYear);
    }
    setModule8DevPlans([]);
    setModule8TrainingPlans([]);
    return { devPlans: [], trainingPlans: [] };
  }, [getActiveAcademicYear]);

  const loadModule9StudentDevelopmentPlan = useCallback(async () => {
    setModule9Loading(true);
    setModule9LoadError("");
    const academicYear = getActiveAcademicYear();
    let nextDevPlans = [];
    try {
      const res = await AdminAPI.acppGetModuleData(MODULE9_DATA_KEY, academicYear);
      nextDevPlans = normalizeModule9DevPlans(res?.dev_plans || []);
    } catch (error) {
      if (!_isNotFoundError(error)) {
        setModule9LoadError("Failed to load saved student development plans for this ACPP year.");
      }
    }
    setModule9DevPlans(nextDevPlans);
    setModule9Loading(false);
    return nextDevPlans;
  }, [getActiveAcademicYear, normalizeModule9DevPlans]);

  const saveModule9StudentDevelopmentPlan = useCallback(async (devPlans) => {
    const academicYear = getActiveAcademicYear();
    if (!_hasValue(academicYear)) {
      throw new Error("ACPP academic year is missing. Please create or activate an ACPP plan.");
    }
    const normalizedDevPlans = normalizeModule9DevPlans(devPlans || []);
    await AdminAPI.acppSaveModuleData(MODULE9_DATA_KEY, academicYear, {
      dev_plans: normalizedDevPlans,
    });
    setModule9DevPlans(normalizedDevPlans);
    return normalizedDevPlans;
  }, [getActiveAcademicYear, normalizeModule9DevPlans]);

  const resetModule9StudentDevelopmentPlan = useCallback(async () => {
    const academicYear = getActiveAcademicYear();
    if (_hasValue(academicYear)) {
      await AdminAPI.acppDeleteModuleData(MODULE9_DATA_KEY, academicYear);
    }
    setModule9DevPlans([]);
    return [];
  }, [getActiveAcademicYear]);

  const loadModule10AcademicCalendar = useCallback(async () => {
    setModule10Loading(true);
    setModule10LoadError("");
    const academicYear = getActiveAcademicYear();
    let nextRows = [];
    let nextCustomEvents = [];
    try {
      const res = await AdminAPI.acppGetModuleData(MODULE10_DATA_KEY, academicYear);
      nextRows = normalizeModule10CalendarRows(res?.calendar_rows || []);
      nextCustomEvents = normalizeModule10CustomEvents(res?.custom_events || []);
    } catch (error) {
      if (!_isNotFoundError(error)) {
        setModule10LoadError("Failed to load saved academic calendar for this ACPP year.");
      }
    }
    setModule10CalendarRows(nextRows);
    setModule10CustomEvents(nextCustomEvents);
    setModule10Loading(false);
    return nextRows;
  }, [getActiveAcademicYear, normalizeModule10CalendarRows, normalizeModule10CustomEvents]);

  const saveModule10AcademicCalendar = useCallback(async (calendarRows, customEvents = []) => {
    const academicYear = getActiveAcademicYear();
    if (!_hasValue(academicYear)) {
      throw new Error("ACPP academic year is missing. Please create or activate an ACPP plan.");
    }
    const normalizedRows = normalizeModule10CalendarRows(calendarRows || []);
    const normalizedCustomEvents = normalizeModule10CustomEvents(customEvents || []);
    await AdminAPI.acppSaveModuleData(MODULE10_DATA_KEY, academicYear, {
      calendar_rows: normalizedRows,
      custom_events: normalizedCustomEvents,
    });
    setModule10CalendarRows(normalizedRows);
    setModule10CustomEvents(normalizedCustomEvents);
    return normalizedRows;
  }, [getActiveAcademicYear, normalizeModule10CalendarRows, normalizeModule10CustomEvents]);

  const resetModule10AcademicCalendar = useCallback(async () => {
    const academicYear = getActiveAcademicYear();
    if (_hasValue(academicYear)) {
      await AdminAPI.acppDeleteModuleData(MODULE10_DATA_KEY, academicYear);
    }
    setModule10CalendarRows([]);
    setModule10CustomEvents([]);
    return [];
  }, [getActiveAcademicYear]);

  const loadModule11DynamicTimetable = useCallback(async () => {
    setModule11Loading(true);
    setModule11LoadError("");
    const academicYear = getActiveAcademicYear();
    let nextState = {
      sectionTimetables: {},
      teacherTimetables: {},
      periodLabels: [],
      workingDays: [],
      periodCount: 6,
    };
    try {
      const res = await AdminAPI.acppGetModuleData(MODULE11_DATA_KEY, academicYear);
      nextState = normalizeModule11State({
        sectionTimetables: res?.section_timetables || {},
        teacherTimetables: res?.teacher_timetables || {},
        periodLabels: res?.period_labels || [],
        workingDays: res?.working_days || [],
        periodCount: res?.period_count || 6,
      });
    } catch (error) {
      if (!_isNotFoundError(error)) {
        setModule11LoadError("Failed to load saved dynamic timetable for this ACPP year.");
      }
    }
    setModule11State(nextState);
    setModule11Loading(false);
    return nextState;
  }, [getActiveAcademicYear, normalizeModule11State]);

  const saveModule11DynamicTimetable = useCallback(async (state) => {
    const academicYear = getActiveAcademicYear();
    if (!_hasValue(academicYear)) {
      throw new Error("ACPP academic year is missing. Please create or activate an ACPP plan.");
    }
    const normalizedState = normalizeModule11State(state);
    await AdminAPI.acppSaveModuleData(MODULE11_DATA_KEY, academicYear, {
      section_timetables: normalizedState.sectionTimetables,
      teacher_timetables: normalizedState.teacherTimetables,
      period_labels: normalizedState.periodLabels,
      working_days: normalizedState.workingDays,
      period_count: normalizedState.periodCount,
    });
    setModule11State(normalizedState);
    return normalizedState;
  }, [getActiveAcademicYear, normalizeModule11State]);

  const resetModule11DynamicTimetable = useCallback(async () => {
    const academicYear = getActiveAcademicYear();
    if (_hasValue(academicYear)) {
      await AdminAPI.acppDeleteModuleData(MODULE11_DATA_KEY, academicYear);
    }
    const emptyState = {
      sectionTimetables: {},
      teacherTimetables: {},
      periodLabels: [],
      workingDays: [],
      periodCount: 6,
    };
    setModule11State(emptyState);
    return emptyState;
  }, [getActiveAcademicYear]);

  const loadModule12LeadershipDashboard = useCallback(async () => {
    setModule12Loading(true);
    setModule12LoadError("");
    const academicYear = getActiveAcademicYear();
    let nextSnapshot = {
      kpi: {},
      healthRows: [],
      chainSteps: [],
      summaryNotes: "",
      updatedAt: "",
      updatedBy: "",
    };
    try {
      const res = await AdminAPI.acppGetModuleData(MODULE12_DATA_KEY, academicYear);
      nextSnapshot = normalizeModule12Snapshot({
        kpi: res?.kpi || {},
        healthRows: res?.health_rows || [],
        chainSteps: res?.chain_steps || [],
        summaryNotes: res?.summary_notes || "",
        updatedAt: res?.updated_at || "",
        updatedBy: res?.updated_by || "",
      });
    } catch (error) {
      if (!_isNotFoundError(error)) {
        setModule12LoadError("Failed to load saved leadership dashboard snapshot for this ACPP year.");
      }
    }
    setModule12Snapshot(nextSnapshot);
    setModule12Loading(false);
    return nextSnapshot;
  }, [getActiveAcademicYear, normalizeModule12Snapshot]);

  const saveModule12LeadershipDashboard = useCallback(async (snapshot) => {
    const academicYear = getActiveAcademicYear();
    if (!_hasValue(academicYear)) {
      throw new Error("ACPP academic year is missing. Please create or activate an ACPP plan.");
    }
    const normalizedSnapshot = normalizeModule12Snapshot(snapshot);
    await AdminAPI.acppSaveModuleData(MODULE12_DATA_KEY, academicYear, {
      kpi: normalizedSnapshot.kpi,
      health_rows: normalizedSnapshot.healthRows,
      chain_steps: normalizedSnapshot.chainSteps,
      summary_notes: normalizedSnapshot.summaryNotes,
    });
    const withMeta = {
      ...normalizedSnapshot,
      updatedAt: new Date().toISOString(),
      updatedBy: _safeStr(localStorage.getItem("user_email") || localStorage.getItem("user_name")).trim(),
    };
    setModule12Snapshot(withMeta);
    return withMeta;
  }, [getActiveAcademicYear, normalizeModule12Snapshot]);

  const resetModule12LeadershipDashboard = useCallback(async () => {
    const academicYear = getActiveAcademicYear();
    if (_hasValue(academicYear)) {
      await AdminAPI.acppDeleteModuleData(MODULE12_DATA_KEY, academicYear);
    }
    const emptySnapshot = {
      kpi: {},
      healthRows: [],
      chainSteps: [],
      summaryNotes: "",
      updatedAt: "",
      updatedBy: "",
    };
    setModule12Snapshot(emptySnapshot);
    return emptySnapshot;
  }, [getActiveAcademicYear]);

  const provisionModule5ToSchool = useCallback(async (academicYear = "") => {
    setModule5ProvisionLoading(true);
    setModule5ProvisionError("");
    try {
      const res = await AdminAPI.acppProvisionModule5(academicYear ? { academic_year: academicYear } : {});
      return res;
    } catch (error) {
      const statusCode = Number(error?.status || error?.statusCode || 0);
      if (statusCode === 404) {
        setModule5ProvisionError("No saved Module 5 data found for the active ACPP year. Save Module 5 first.");
        return {
          status: "no_data",
          message: "No saved Module 5 data found for the active ACPP year.",
          classes: { summary: { created: 0, updated: 0, skipped: 0, conflicts: 0 } },
          departments: { summary: { created: 0, updated: 0, skipped: 0, conflicts: 0 } },
        };
      }
      setModule5ProvisionError(error?.message || "Failed to provision classes and departments.");
      return null;
    } finally {
      setModule5ProvisionLoading(false);
    }
  }, []);

  const provisionModule7Assessments = useCallback(async (academicYear = "") => {
    setModule7ProvisionLoading(true);
    setModule7ProvisionError("");
    try {
      const res = await AdminAPI.acppProvisionModule7Assessments(academicYear ? { academic_year: academicYear } : {});
      return res;
    } catch (error) {
      const statusCode = Number(error?.status || error?.statusCode || 0);
      if (statusCode === 404) {
        setModule7ProvisionError("No saved Module 7 data found for the active ACPP year. Save Module 7 first.");
        return {
          status: "no_data",
          message: "No saved Module 7 data found for the active ACPP year.",
          summary: { created: 0, updated: 0, skipped: 0, conflicts: 0 },
          created: [],
          updated: [],
          skipped: [],
          conflicts: [],
        };
      }
      setModule7ProvisionError(error?.message || "Failed to provision Module 7 assessments.");
      return null;
    } finally {
      setModule7ProvisionLoading(false);
    }
  }, []);

  useEffect(() => {
    loadACPPAcademicYear();
  }, [loadACPPAcademicYear]);

  useEffect(() => {
    loadSchoolMasterFromSources();
    loadModule3Grades();
    loadModule4AcademicPlanning();
    loadModule5AcademicOwnership();
    loadModule5StaffOptions();
    loadModule6TeacherWorkflow();
    loadModule7AssessmentStructure();
    loadModule8TeacherDevelopmentTraining();
    loadModule9StudentDevelopmentPlan();
    loadModule10AcademicCalendar();
    loadModule11DynamicTimetable();
    loadModule12LeadershipDashboard();
  }, [loadSchoolMasterFromSources, loadModule3Grades, loadModule4AcademicPlanning, loadModule5AcademicOwnership, loadModule5StaffOptions, loadModule6TeacherWorkflow, loadModule7AssessmentStructure, loadModule8TeacherDevelopmentTraining, loadModule9StudentDevelopmentPlan, loadModule10AcademicCalendar, loadModule11DynamicTimetable, loadModule12LeadershipDashboard]);

  // Derived hero stats
  const heroGradesCount = selectedGrades.length;
  const heroSubjectsCount = [...new Set(subjectMappings.map(m => m.subjectName).filter(Boolean))].length;
  const heroProgramsCount = module5OwnershipMappings.length;

  // Helper: get all staff from module5OwnershipMappings
  const getAllStaff = useCallback(() => {
    const people = new Set();
    module5OwnershipMappings.forEach(item => {
      (item.teachers || []).forEach(t => { if (t) people.add(t); });
      (item.hods || []).forEach(h => { if (h) people.add(h); });
    });
    return [...people].sort();
  }, [module5OwnershipMappings]);

  // Helper: get sections for a grade from module5SectionMappings
  const getSectionsForGrade = useCallback((grade) => {
    const match = module5SectionMappings.find(r => r.grade === grade);
    return match ? match.finalSections : [];
  }, [module5SectionMappings]);

  // Helper: get subjects for a grade from subjectMappings
  const getSubjectsForGrade = useCallback((grade) => {
    return [...new Set(
      subjectMappings
        .filter(s => s.subjectName && Array.isArray(s.grades) && s.grades.includes(grade))
        .map(s => s.subjectName)
    )];
  }, [subjectMappings]);

  // Helper: get subject meta (categories + types) for grade+subject
  const getSubjectMeta = useCallback((grade, subjectName) => {
    const matched = subjectMappings.filter(s =>
      s.subjectName === subjectName && Array.isArray(s.grades) && s.grades.includes(grade)
    );
    return {
      categories: [...new Set(matched.flatMap(s => s.categories || []))],
      types: [...new Set(matched.flatMap(s => s.finalTypes || s.types || []))],
    };
  }, [subjectMappings]);

  const createEmptyM6Plan = useCallback((base) => ({
    uid: base.uid,
    academicYear: getActiveAcademicYear(),
    grade: base.grade,
    section: base.section,
    subject: base.subject,
    teachers: [...(base.teachers || [])],
    hods: [...(base.hods || [])],
    categories: [...(base.categories || [])],
    types: [...(base.types || [])],
    teacherStatus: "Draft",
    hodStatus: "Pending",
    hodRemarks: "",
    adminRemarks: "",
    remarksThread: [],
    submittedAt: "",
    approvedAt: "",
    lastUpdated: "",
    terms: MODULE6_TERMS.map(termNo => ({ termNo, units: [] })),
  }), [getActiveAcademicYear]);

  const getM6FlatRows = useCallback(() => {
    const rows = [];
    Object.values(module6Plans).forEach(plan => {
      (plan.terms || []).forEach(term => {
        term.units.forEach(unit => {
          unit.chapters.forEach(chapter => {
            const topicsText = (chapter.topics || []).length
              ? chapter.topics.map(t => {
                  const subs = (t.subtopics || []).filter(Boolean).join(", ");
                  return subs ? `${t.topicName || "Topic"} (${subs})` : (t.topicName || "Topic");
                }).join(" | ")
              : "—";
            const pedagogy = [...new Set([...(chapter.pedagogies || []), ...(chapter.customPedagogies || []).filter(Boolean)])];
            const competencies = [...new Set([...(chapter.competencies || []), ...(chapter.customCompetencies || []).filter(Boolean)])];
            const objectives = (chapter.learningObjectives || []).filter(Boolean);
            rows.push({
              grade: plan.grade, section: plan.section, subject: plan.subject,
              academicYear: plan.academicYear,
              term: term.termNo,
              unitNo: unit.unitNo || "—",
              unitName: unit.unitName || "—",
              chapterNo: chapter.chapterNo || "—",
              chapterName: chapter.chapterName || "—",
              topics: topicsText,
              pedagogy: pedagogy.length ? pedagogy.join(", ") : "—",
              objectives: objectives.length ? objectives.join(" • ") : "—",
              competencies: competencies.length ? competencies.join(", ") : "—",
            });
          });
        });
      });
    });
    return rows;
  }, [module6Plans]);

  // Module 10 — generate calendar from M6 + M7
  const generateCalendar = useCallback((filters = {}) => {
    const planningRows = getM6FlatRows();
    const assessmentRows = module7Assessments;
    const academicYear = schoolMaster.year;

    if (!planningRows.length) return [];

    const combined = [];
    planningRows.forEach(plan => {
      const chapterLabel = plan.chapterNo && plan.chapterNo !== "—"
        ? `${plan.chapterNo}. ${plan.chapterName}`
        : plan.chapterName;

      const matched = assessmentRows.filter(a =>
        a.grade === plan.grade &&
        a.section === plan.section &&
        a.subject === plan.subject &&
        Array.isArray(a.chaptersCovered) &&
        a.chaptersCovered.some(c =>
          c === chapterLabel ||
          c === plan.chapterName ||
          c.includes(plan.chapterName) ||
          plan.chapterName.includes(c.replace(/^\d+\.\s*/, ""))
        )
      );

      const termLabel = `Term ${plan.term}`;

      if (matched.length) {
        matched.forEach(assessment => {
          const subTypes = Array.isArray(assessment.assessmentSubTypes)
            ? assessment.assessmentSubTypes.join(", ")
            : (assessment.assessmentSubTypes || "—");
          combined.push({
            academicYear,
            grade: plan.grade, section: plan.section, subject: plan.subject,
            term: termLabel,
            unitNo: plan.unitNo, unitName: plan.unitName,
            chapter: plan.chapterName,
            assessmentTenure: assessment.tenure || "—",
            assessmentType: assessment.assessmentType || "—",
            assessmentSubTypes: subTypes || "—",
          });
        });
      } else {
        combined.push({
          academicYear,
          grade: plan.grade, section: plan.section, subject: plan.subject,
          term: termLabel,
          unitNo: plan.unitNo, unitName: plan.unitName,
          chapter: plan.chapterName,
          assessmentTenure: "—", assessmentType: "—", assessmentSubTypes: "—",
        });
      }
    });

    return combined.filter(row =>
      (!filters.grade || row.grade === filters.grade) &&
      (!filters.section || row.section === filters.section) &&
      (!filters.subject || row.subject === filters.subject) &&
      (!filters.term || row.term === filters.term)
    );
  }, [getM6FlatRows, module7Assessments, schoolMaster.year]);

  const value = {
    // State
    acppAcademicYear, setAcppAcademicYear,
    schoolMaster, setSchoolMaster,
    module1Loading, module1LoadError, module1MissingFields,
    module3Loading, module3LoadError,
    module4Loading, module4LoadError,
    module5Loading, module5LoadError,
    module5TeacherOptions, module5HodOptions, module5StaffLoading, module5StaffLoadError,
    module5ProvisionLoading, module5ProvisionError,
    module7ProvisionLoading, module7ProvisionError,
    module6Loading, module6LoadError, module6Saving, module6SaveError, module6ActionLoading, module6ActionError,
    module7Loading, module7LoadError,
    module8Loading, module8LoadError,
    module9Loading, module9LoadError,
    module10Loading, module10LoadError,
    module11Loading, module11LoadError,
    module12Loading, module12LoadError,
    currentUserRole, canEditModules1to5, canEditModule6,
    selectedGrades, setSelectedGrades,
    subjectMappings, setSubjectMappings,
    module5SectionMappings, setModule5SectionMappings,
    module5OwnershipMappings, setModule5OwnershipMappings,
    module6Plans, setModule6Plans,
    module7Assessments, setModule7Assessments,
    module8DevPlans, setModule8DevPlans,
    module8TrainingPlans, setModule8TrainingPlans,
    module9DevPlans, setModule9DevPlans,
    module10CalendarRows, setModule10CalendarRows,
    module10CustomEvents, setModule10CustomEvents,
    module11State, setModule11State,
    module12Snapshot, setModule12Snapshot,
    // Hero
    heroGradesCount, heroSubjectsCount, heroProgramsCount,
    // Helpers
    getAllStaff,
    getSectionsForGrade,
    getSubjectsForGrade,
    getSubjectMeta,
    loadSchoolMasterFromSources,
    saveSchoolMasterPriority,
    resetSchoolMasterPriority,
    loadACPPAcademicYear,
    loadModule3Grades,
    saveModule3Grades,
    resetModule3Grades,
    loadModule4AcademicPlanning,
    saveModule4AcademicPlanning,
    resetModule4AcademicPlanning,
    loadModule5AcademicOwnership,
    loadModule5StaffOptions,
    saveModule5AcademicOwnership,
    resetModule5AcademicOwnership,
    provisionModule5ToSchool,
    provisionModule7Assessments,
    loadModule6TeacherWorkflow,
    saveModule6TeacherWorkflow,
    resetModule6TeacherWorkflow,
    module6HodSubmitToAdmin,
    module6HodSendBackToTeacher,
    module6AdminApprove,
    module6AdminSendBackToHod,
    module6SubmitToHod,
    loadModule7AssessmentStructure,
    saveModule7AssessmentStructure,
    resetModule7AssessmentStructure,
    loadModule8TeacherDevelopmentTraining,
    saveModule8TeacherDevelopmentTraining,
    resetModule8TeacherDevelopmentTraining,
    loadModule9StudentDevelopmentPlan,
    saveModule9StudentDevelopmentPlan,
    resetModule9StudentDevelopmentPlan,
    loadModule10AcademicCalendar,
    saveModule10AcademicCalendar,
    resetModule10AcademicCalendar,
    loadModule11DynamicTimetable,
    saveModule11DynamicTimetable,
    resetModule11DynamicTimetable,
    loadModule12LeadershipDashboard,
    saveModule12LeadershipDashboard,
    resetModule12LeadershipDashboard,
    getActiveAcademicYear,
    createEmptyM6Plan,
    getM6FlatRows,
    generateCalendar,
    MODULE6_TERMS,
  };

  return <ACPPContext.Provider value={value}>{children}</ACPPContext.Provider>;
}

export function useACPP() {
  const ctx = useContext(ACPPContext);
  if (!ctx) throw new Error("useACPP must be used inside ACPPProvider");
  return ctx;
}
